import React, { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Car, Accessibility, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Calendar, Sparkles, Shield, BookOpen, MessageCircle, Brain, Target, Bed, Apple, Flower2, Home, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import NavBar from '@/components/NavBar';
// Removed Footer import - App.tsx handles global footer
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import receptionImageWebP from '@/assets/reception.webp';
import receptionImage from '@/assets/reception.jpg';
import treatmentImageWebP from '@/assets/treatment.webp';
import treatmentImage from '@/assets/treatment.jpg';

const CurrimundiClinicPage = () => {
  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Women's Wellness Clinic Currimundi | Women and the Way";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Visit our Currimundi clinic on the Sunshine Coast for acupuncture, Chinese medicine, and women's health support. Book a session today.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Women's Wellness Clinic Currimundi | Women and the Way");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Visit our Currimundi clinic on the Sunshine Coast for acupuncture, Chinese medicine, and women's health support. Book a session today.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/currimundi-clinic");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/watw_logo.jpg");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Women's Wellness Clinic Currimundi | Women and the Way");
    }

    // Add LocalBusiness Schema for Currimundi location
    const currimundiSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Currimundi Clinic",
      "image": "https://womenandtheway.com.au/watw_logo.jpg",
      "description": "Women's wellness clinic in Currimundi offering acupuncture, Chinese medicine, and holistic health support for women at every life stage.",
      "url": "https://womenandtheway.com.au/currimundi-clinic",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU",
        "postalCode": "4551"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.7833,
        "longitude": 153.1167
      },
      "priceRange": "$$",
      "telephone": "+61415537784",
      "email": "womenandtheway@gmail.com",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(currimundiSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = () => {
    window.open(BOOKING_URL, '_blank');
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Visit Us at Our Currimundi Clinic
              </h1>
              <p className="text-xl md:text-2xl text-sage-600 mb-4 font-medium">
                Your sanctuary for women's wellness on the Sunshine Coast
              </p>
            </div>

            {/* Hero Image */}
            <div className="mb-12">
              <picture>
                <source srcSet={receptionImageWebP} type="image/webp" />
                <img 
                  src={receptionImage} 
                  alt="Currimundi Clinic Reception" 
                  className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-lg"
                />
              </picture>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12 mb-8">
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  At Women and the Way, our Currimundi clinic is more than just a space for acupuncture and herbal medicine - it's a place to exhale, to be held, and to reconnect with your natural rhythm. Whether you're navigating fertility, pregnancy, perimenopause, emotional stress, or physical pain, we're here to support you with gentle, effective care rooted in Traditional Chinese Medicine.
                </p>
              </div>
            </div>

            {/* Clinic Location Section */}
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 rounded-2xl border-2 border-sage-200/50 p-8 md:p-12 mb-8">
              <h2 className="font-serif text-3xl font-semibold mb-6 flex items-center gap-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                <Leaf className="w-7 h-7 text-sage-600" />
                Our Clinic Location
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sage-800">Address:</p>
                      <p className="text-sage-700">700 Nicklin Way, Currimundi QLD 4551</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-sage-600" />
                    <a href="tel:0415537784" className="text-sage-700 hover:text-amber-600 transition-colors">
                      Phone: 0415 537 784
                    </a>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-sage-600" />
                    <a href="mailto:womenandtheway@gmail.com" className="text-sage-700 hover:text-amber-600 transition-colors">
                      Email: womenandtheway@gmail.com
                    </a>
                  </div>

                  <div className="flex items-start gap-3">
                    <Car className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                    <p className="text-sage-700">Parking: Free onsite parking available</p>
                  </div>

                  <div className="flex items-start gap-3">
                    <Accessibility className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                    <p className="text-sage-700">Accessibility: Ground-floor access, wheelchair accessible</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md border border-sage-200">
                  <Button 
                    variant="outline"
                    className="w-full border-sage-600 text-sage-700 hover:bg-sage-600 hover:text-white mb-4"
                    onClick={() => window.open('https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD+4551', '_blank')}
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    View on Google Maps
                  </Button>
                  <p className="text-sm text-sage-600">
                    Easily accessible from Caloundra, Kawana, Birtinya, Wurtulla, and surrounding areas - your care is always close to home.
                  </p>
                </div>
              </div>
            </div>

            {/* Why Visit Us Section */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12 mb-8">
              <h2 className="font-serif text-3xl font-semibold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Why Visit Us in Currimundi?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-amber-200/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Heart className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-sage-800 mb-2">Heart-Centred Practitioners</h3>
                        <p className="text-sage-700">We're women caring for women - offering compassionate, personalised care that honours your story and supports your body at every life stage.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Leaf className="w-6 h-6 text-sage-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-sage-800 mb-2">Traditional Chinese Medicine with a Modern Soul</h3>
                        <p className="text-sage-700">From acupuncture to herbal medicine, gua sha to womb care, we blend ancient techniques with evidence-informed practice to help you heal naturally.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Star className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-sage-800 mb-2">A Space to Be Still</h3>
                        <p className="text-sage-700">Our Currimundi clinic is intentionally designed to feel like a retreat - soft lighting, gentle music, warm tea, and space to land.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-6 h-6 text-sage-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-sage-800 mb-2">Convenient Location on the Sunshine Coast</h3>
                        <p className="text-sage-700">Easily accessible from Caloundra, Kawana, Birtinya, Wurtulla, and surrounding areas - your care is always close to home.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Treatment Room Image */}
            <div className="mb-8">
              <picture>
                <source srcSet={treatmentImageWebP} type="image/webp" />
                <img
                  src={treatmentImage}
                  alt="Treatment Room at Currimundi Clinic"
                  className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-lg"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>

            {/* What We Treat Section */}
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 rounded-2xl border-2 border-sage-200/50 p-8 md:p-12 mb-8">
              <h2 className="font-serif text-3xl font-semibold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What We Treat at the Clinic
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Hormonal health, irregular cycles, and PMS</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Fertility, IVF support, and preconception care</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Pregnancy and postnatal support</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Perimenopause and menopause transitions</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Stress, anxiety, insomnia, and burnout</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Skin conditions and cosmetic acupuncture</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Pain and injury: back, neck, shoulder, pelvic</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Digestive issues and immune support</p>
                </div>
              </div>
              <p className="text-sage-700 mt-6 italic">
                All treatments are tailored to your unique pattern, body, and phase of life.
              </p>
            </div>

            {/* CTA Section */}
            <div className="bg-sage-800 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="font-serif text-3xl font-bold text-white mb-4">
                Ready to Feel More Like You?
              </h2>
              <p className="text-lg text-sage-100 mb-8">
                We invite you to experience what it's like to be seen, supported, and truly cared for. Whether you're coming in for your first acupuncture session, a fertility plan, or simply a moment to reset - our doors are open.
              </p>
              
              <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-6">
                <Button 
                  size="lg"
                  onClick={handleBooking}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-semibold rounded-full"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book an appointment today
                </Button>
              </div>
              
              <div className="bg-sage-700/50 rounded-lg p-4">
                <p className="text-sm text-sage-200">
                  700 Nicklin Way, Currimundi | Sunshine Coast
                </p>
                <p className="text-sm text-sage-200 mt-2">
                  Because your wellness deserves a space that feels like home.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
              {/* Footer removed - App.tsx handles global footer */}
      <ScrollDownArrow />
      <ReturnToTop />
    </>
  );
};

export default CurrimundiClinicPage; 